import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Sparkles, FileText, Loader2, Copy } from "lucide-react";

function renderMarkdown(text) {
  // lightweight markdown -> HTML for headings, bold, bullets
  return text
    .split("\n")
    .map((line, i) => {
      let l = line.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      if (/^#{1,3}\s/.test(l)) return `<h3 class="font-heading text-lg mt-4 mb-1">${l.replace(/^#{1,3}\s/, "")}</h3>`;
      if (/^\s*[-*]\s/.test(l)) return `<li class="ml-5 list-disc text-sm my-1">${l.replace(/^\s*[-*]\s/, "")}</li>`;
      if (l.trim() === "") return "<br/>";
      return `<p class="text-sm my-1">${l}</p>`;
    })
    .join("");
}

export default function Insights() {
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState("");
  const [context, setContext] = useState("");

  const generate = async (kind) => {
    setBusy(kind); setResult("");
    try {
      const { data } = await api.post("/ai/insights", { kind, context });
      setResult(data.result);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  return (
    <div>
      <PageHeader title="AI Insights" subtitle="Turn your live financial data into analysis and board-ready narratives." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-4">
          <textarea
            value={context} onChange={(e) => setContext(e.target.value)} rows={4}
            data-testid="ai-context"
            placeholder="Optional context (e.g. 'focus on cash runway and fundraising')"
            className="w-full bg-white border border-stone-300 rounded-lg px-3.5 py-3 text-sm outline-none focus:border-forest focus:ring-2 focus:ring-forest/15"
          />
          <button onClick={() => generate("insights")} disabled={!!busy} data-testid="gen-insights-btn"
            className="w-full bg-forest hover:bg-forest-900 text-white rounded-md px-4 py-3 text-sm font-medium transition-colors duration-200 flex items-center justify-center disabled:opacity-60">
            {busy === "insights" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Financial Insights
          </button>
          <button onClick={() => generate("board_report")} disabled={!!busy} data-testid="gen-board-btn"
            className="w-full bg-white border border-stone-300 text-stone-800 hover:bg-stone-50 rounded-md px-4 py-3 text-sm font-medium transition-colors duration-200 flex items-center justify-center disabled:opacity-60">
            {busy === "board_report" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
            Board Report Narrative
          </button>
          <p className="text-xs text-[#5C5C54]">Powered by Claude · analyzes your current dashboard figures in real time.</p>
        </div>

        <div className="lg:col-span-2 bg-white border border-stone-200 rounded-xl p-6 min-h-[400px] relative">
          {result ? (
            <>
              <button onClick={() => { navigator.clipboard.writeText(result); toast.success("Copied"); }}
                className="absolute top-4 right-4 text-[#5C5C54] hover:text-forest"><Copy className="h-4 w-4" /></button>
              <div data-testid="ai-result" className="prose-sm text-[#1A1A1A]" dangerouslySetInnerHTML={{ __html: renderMarkdown(result) }} />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-[360px] text-center text-[#5C5C54]">
              <Sparkles className="h-8 w-8 mb-3 text-forest-muted" />
              <p className="text-sm max-w-xs">Generate financial insights or a board report to see AI analysis of your foundation's finances here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
